// Story 6.9 (ADO-1163, AC2) — Icon set.
//
// Self-contained inline SVGs keyed to the Figma "🧱 Icons / Phosphor" set
// (node 593-2). Rendered from a path registry so icons are tree-shakeable by
// name and inherit `currentColor` (no remote assets, CSP-safe). Stroke style
// (24×24, 1.75 stroke, round caps) approximates the Phosphor "regular" weight;
// swap the registry for exact Figma SVG exports without changing call sites.
//
// a11y (NFR24–26): decorative by default (`aria-hidden`, focusable={false}).
// Pass `title` to promote an icon to an accessible graphic (role="img").

import type { ReactElement, SVGProps } from 'react';

export type IconName =
  | 'user'
  | 'users'
  | 'userPlus'
  | 'download'
  | 'trash'
  | 'edit'
  | 'x'
  | 'plus'
  | 'minus'
  | 'check'
  | 'checkCircle'
  | 'xCircle'
  | 'warning'
  | 'info'
  // Story 11.10 (ADO-1961): Phosphor Question, the Info Button's glyph (the margin info panel).
  | 'question'
  | 'bell'
  | 'mail'
  | 'caretDown'
  | 'caretRight'
  | 'caretLeft'
  | 'search'
  | 'eye'
  | 'eyeOff'
  | 'clock'
  | 'heart'
  | 'tag'
  // Pricing migration 32 (ADO-3985): the multi-buy strip's Phosphor Gift (regular).
  | 'gift'
  | 'cart'
  | 'history'
  // Epic 7 re-skin (ADO-903) — FilterChips "Clear filters" (prototype Phosphor
  // ClockCounterClockwise; same drawing as `history`).
  | 'clockCounterClockwise'
  // Epic 7 re-skin (ADO-903) — SiteHeader controls.
  | 'menu'
  | 'storefront'
  | 'camera'
  | 'chartBar'
  // Epic 7 re-skin (ADO-903) — listing grid/list view-mode toggle.
  | 'gridView'
  | 'listView'
  | 'sliders'
  // Epic 7 pixel parity (ADO-903) — search typeahead "View all" arrow.
  | 'arrowRight'
  | 'arrowDown'
  // ADO-1779: the mobile order-history "Return to products" text link (Figma 5419:23790).
  | 'arrowLeft'
  // Story 9.6 (ADO-1080) — the order-history Filters panel (Figma 4925:50646).
  // Phosphor Calendar, on the two date fields.
  | 'calendar'
  // Phosphor ArrowsClockwise, on the "Clear filters" action. Distinct from
  // `clockCounterClockwise` (Phosphor ClockCounterClockwise), which the Epic 7
  // catalogue FilterChips uses for the same words: the two panels were drawn with
  // different glyphs and each follows its own frame.
  | 'arrowsClockwise'
  // ADO-1779 — the header "Depots" control (Figma Home Button 4111:12619): four
  // filled rounded tiles, not the outlined `gridView` toggle.
  | 'depots'
  // Epic 9 UI polish — Phosphor Package, leading the depot name on the cart page's depot
  // card. (The order-history depot pill now draws each depot's own icon, ADO-1779.)
  | 'package'
  // Story 8.6 (ADO-2015) — the order confirmation (Figma 5464:87367 / 5464:87949): the Phosphor
  // Check in the success badge.
  | 'checkBold'
  // Story 8.12 (ADO-2021) — Phosphor Truck, leading the on-order chip (Figma 4977:1802).
  | 'truck'
  // Story 8.7 (ADO-2016) — the re-add toast (Figma 5943:31353 / 5943:31545): Phosphor
  // CheckCircle leading it and Phosphor ShoppingCart closing it.
  | 'checkCircleBold'
  | 'shoppingCart'
  // ADO-1779 — Phosphor ArrowUp (Figma 595:16), in the floating Back to Top button.
  | 'arrowUp'
  // Story 8.10 (ADO-2019): the HHT surfaces (the 8.10 Figma exports): the header
  // "HHT Orders" device mark (Figma layer "Smartphone", drawn with the top and bottom
  // bands of Phosphor DeviceMobile), the depot marks (the Lucide-derived ShoppingBasket
  // for Ambient, and the Lucide Thermometer and Wine geometry the Figma layers use for
  // Chill and Alcohol) and the history card's collapse chevron.
  | 'shoppingBasket'
  | 'deviceMobile'
  | 'thermometer'
  | 'wine'
  | 'caretUp'
  // Enabler E7-C (ADO-2274), D-16: the Leaf leading the "Picked fresh" pill (Figma 7298:215034).
  | 'leaf'
  // Story 7.33 (ADO-2026): the header "Planogram" entry (Figma Planogram Button, two stacked
  // shelves: Phosphor Rows) and the plan's "View full size" control (Phosphor CornersOut).
  | 'rows'
  | 'cornersOut'
  // The RBP core marks. Figma has none, so SL1.0's two glyphs are drawn here: a filled dot in a
  // ring for "Core" (`fa-dot-circle-o`) and a chain link for "RBP - Link" (`fa-link`).
  | 'rbpCore'
  | 'rbpLink'
  // The RBP non-payment mark (Figma CurrencyEurProhibit).
  | 'currencyEurProhibit';

// Clock face with a counter-clockwise sweep arrow — shared by `history` and its
// Phosphor-named alias `clockCounterClockwise` (Epic 7 re-skin, ADO-903).
const clockCounterClockwiseGlyph = (
  <>
    <path d="M4 12a8 8 0 1 0 2.5-5.8" />
    <path d="M4 4v3.5h3.5" />
    <path d="M12 8v4.3l3 1.7" />
  </>
);

// Each entry is the inner markup of a 24×24 viewBox, stroke = currentColor.
const PATHS: Record<IconName, ReactElement> = {
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 19c0-3 2.9-5.2 6.5-5.2S15.5 16 15.5 19" />
      <path d="M16 5.2A3.5 3.5 0 0 1 16 12" />
      <path d="M17 13.9c2.7.4 4.5 2.5 4.5 5.1" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="9" cy="8" r="3.75" />
      <path d="M2.5 20c0-3.2 2.9-5.5 6.5-5.5s6.5 2.3 6.5 5.5" />
      <path d="M18 8v6M15 11h6" />
    </>
  ),
  download: (
    <>
      <path d="M12 3v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M4 20h16" />
    </>
  ),
  trash: (
    <>
      <path d="M4 6h16" />
      <path d="M9 6V4h6v2" />
      <path d="M6 6l1 14h10l1-14" />
      <path d="M10 10v6M14 10v6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4L18.5 9.5a2 2 0 0 0-2.8-2.8L4.5 18v2z" />
      <path d="M14 8l3 3" />
    </>
  ),
  x: <path d="M6 6l12 12M18 6L6 18" />,
  // Story 7.18 redesign — quantity stepper (SCAN-04).
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  check: <path d="M5 12.5l5 5 9-11" />,
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.5 2.5L16 9" />
    </>
  ),
  xCircle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.5L21 19H3l9-15.5z" />
      <path d="M12 9v5" />
      <circle cx="12" cy="16.8" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <circle cx="12" cy="7.8" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  question: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.75 9.5a2.25 2.25 0 1 1 3 2.12c-.45.16-.75.58-.75 1.06v.57" />
      <circle cx="12" cy="16.6" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M4 7l8 6 8-6" />
    </>
  ),
  caretDown: <path d="M6 9l6 6 6-6" />,
  caretUp: <path d="M6 15l6-6 6 6" />,
  // Enabler E7-C (ADO-2274), D-16: the exact Figma export (Leaf in "InStockPill / Picked fresh",
  // 7298:215034), FILLED like `truck`, drawn on a 12px grid (scaled 2x onto the 24px viewBox).
  leaf: (
    <g transform="scale(2)" fill="currentColor" stroke="none">
      <path d="M10.0001 5C10.0001 3.60469 9.72057 2.68491 9.36976 1.89062C9.00205 2.24857 8.60747 2.52007 8.09193 2.74268C7.36457 3.05673 6.40969 3.26796 4.99476 3.54102C4.99125 3.54169 4.98754 3.54237 4.98402 3.54297C4.24232 3.66973 3.57538 4.07055 3.11537 4.66602C2.65536 5.26151 2.43596 6.00813 2.50062 6.75781C2.56535 7.50754 2.90921 8.20598 3.46449 8.71387C4.0198 9.22176 4.74612 9.50228 5.49867 9.5H5.50013L5.71888 9.49414C7.97553 9.37253 10.0001 7.38792 10.0001 5ZM11.0001 5C11.0001 8.03472 8.37776 10.4989 5.50111 10.4995L5.50013 10.5C4.49723 10.5027 3.52976 10.1285 2.78968 9.45166C2.04934 8.77451 1.59037 7.84383 1.50404 6.84424C1.41774 5.84456 1.71046 4.84876 2.32386 4.05469C2.93509 3.26344 3.82045 2.73004 4.80531 2.55908L5.31654 2.45898C6.44793 2.23274 7.16159 2.0552 7.69544 1.82471C8.27087 1.57625 8.64727 1.26103 9.11097 0.686035L9.15345 0.639648C9.25928 0.537857 9.40574 0.486946 9.55384 0.50293C9.72294 0.521247 9.87133 0.624226 9.9474 0.776367C10.4634 1.80836 11.0001 2.97927 11.0001 5Z" />
      <path d="M0.5 10.5C0.5 8.75741 1.59761 7.37539 3.44287 7.00977C3.9864 6.90194 4.55402 6.67288 5.0498 6.40674C5.54946 6.1385 5.94221 5.85076 6.14648 5.64648C6.34175 5.45122 6.65825 5.45122 6.85352 5.64648C7.04878 5.84175 7.04878 6.15825 6.85352 6.35352C6.55784 6.6492 6.07567 6.99136 5.52295 7.28809C5.03583 7.54958 4.4676 7.78999 3.88672 7.93457L3.63721 7.99023C2.25247 8.26461 1.5 9.24259 1.5 10.5C1.5 10.7761 1.27614 11 1 11C0.723858 11 0.5 10.7761 0.5 10.5Z" />
    </g>
  ),
  // The RBP non-payment mark: the exact Figma export (CurrencyEurProhibit, 8148:2), FILLED like
  // `leaf`, its 20.25px vector placed at (1.875, 1.875) on the 24px frame.
  currencyEurProhibit: (
    <g transform="translate(1.875 1.875)" fill="currentColor" stroke="none">
      <path d="M7.80469 9.75H6.95703C6.94568 9.87434 6.9375 9.99943 6.9375 10.125C6.93751 10.2506 6.94566 10.3756 6.95703 10.5H8.55469L10.0547 12H7.3916C7.46447 12.1427 7.54319 12.2829 7.63281 12.417C8.08605 13.0952 8.73075 13.6243 9.48438 13.9365C10.2379 14.2485 11.0673 14.3299 11.8672 14.1709C11.9646 14.1515 12.0609 14.1278 12.1562 14.1016L13.3271 15.2725C12.9544 15.4364 12.5636 15.5613 12.1602 15.6416C11.0692 15.8586 9.93787 15.7479 8.91016 15.3223C7.88236 14.8965 7.00382 14.175 6.38574 13.25C6.12446 12.859 5.91495 12.4387 5.75977 12H4.875C4.46084 12 4.12508 11.6641 4.125 11.25C4.125 10.8358 4.46079 10.5 4.875 10.5H5.45117C5.44284 10.3754 5.4375 10.2504 5.4375 10.125C5.4375 9.99965 5.44285 9.87452 5.45117 9.75H4.875C4.46084 9.75 4.12508 9.41414 4.125 9C4.125 8.58579 4.46079 8.25 4.875 8.25H5.75977C5.80631 8.1184 5.85976 7.98931 5.91602 7.86133L7.80469 9.75Z" />
      <path d="M14.8555 12.9102C14.9214 12.9446 14.9847 12.9867 15.04 13.042C15.095 13.097 15.1366 13.1601 15.1709 13.2256L14.8555 12.9102Z" />
      <path d="M11.25 8.25C11.6642 8.25 12 8.58579 12 9C11.9999 9.28589 11.8379 9.53069 11.6025 9.65723L10.1953 8.25H11.25Z" />
      <path d="M8.91016 4.92773C9.93789 4.50212 11.0691 4.39138 12.1602 4.6084C13.2512 4.82548 14.2535 5.3609 15.04 6.14746C15.3329 6.44032 15.3328 6.91512 15.04 7.20801C14.7471 7.5007 14.2723 7.50084 13.9795 7.20801C13.4026 6.63117 12.6673 6.23828 11.8672 6.0791C11.0672 5.92003 10.238 6.00235 9.48438 6.31445C9.20437 6.43045 8.93907 6.5758 8.69336 6.74805L7.62109 5.67578C8.01367 5.37196 8.4467 5.11972 8.91016 4.92773Z" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.125 0C15.7169 0 20.25 4.53312 20.25 10.125C20.25 15.7169 15.7169 20.25 10.125 20.25C4.53312 20.25 0 15.7169 0 10.125C0 4.53312 4.53312 0 10.125 0ZM3.51953 4.58008C2.25959 6.07943 1.5 8.0133 1.5 10.125C1.5 14.8885 5.36154 18.75 10.125 18.75C12.2367 18.75 14.1696 17.9894 15.6689 16.7295L3.51953 4.58008ZM10.125 1.5C8.0133 1.5 6.07943 2.25959 4.58008 3.51953L16.7295 15.6689C17.9894 14.1696 18.75 12.2367 18.75 10.125C18.75 5.36154 14.8885 1.5 10.125 1.5Z"
      />
    </g>
  ),
  rbpCore: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" />
    </>
  ),
  rbpLink: (
    <>
      <path d="M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1 1" />
      <path d="M13.5 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1-1" />
    </>
  ),
  rows: (
    <>
      <rect x="4" y="4.5" width="16" height="6.5" rx="1.5" />
      <rect x="4" y="13" width="16" height="6.5" rx="1.5" />
    </>
  ),
  cornersOut: <path d="M15 4h5v5M9 20H4v-5M20 15v5h-5M4 9V4h5" />,
  caretRight: <path d="M9 6l6 6-6 6" />,
  // Story 7.12 (ADO-911): the RecommendationRail "previous" control (mirror of caretRight).
  caretLeft: <path d="M15 6l-6 6 6 6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4.5-4.5" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M10.6 6.1A9.6 9.6 0 0 1 12 6c6.5 0 10 6 10 6a13.4 13.4 0 0 1-2.9 3.4M6.5 6.6A13.3 13.3 0 0 0 2 12s3.5 6 10 6a9.6 9.6 0 0 0 3.9-.8" />
      <path d="M4 4l16 16" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  heart: (
    <path d="M12 20S3.5 14.5 3.5 8.8A4.3 4.3 0 0 1 12 6a4.3 4.3 0 0 1 8.5 2.8C20.5 14.5 12 20 12 20z" />
  ),
  tag: (
    <>
      <path d="M3.5 12.5l8-8H20v8.5l-8 8-8.5-8.5z" />
      <circle cx="16" cy="8" r="1.3" />
    </>
  ),
  // Phosphor Gift (regular), approximated in the registry's stroke style: the lid, the box under
  // it, the ribbon down the middle and the bow on top.
  gift: (
    <>
      <rect x="3.5" y="8" width="17" height="4" rx="1" />
      <path d="M5 12v7.5a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5V12" />
      <path d="M12 8v12" />
      <path d="M12 8c-1-2.5-4.5-4-5.3-1.8C6.1 8 9.6 8 12 8zm0 0c1-2.5 4.5-4 5.3-1.8.6 1.8-2.9 1.8-5.3 1.8z" />
    </>
  ),
  cart: (
    <>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
      <path d="M3 4h2l2.2 11h10.3l1.8-8H6" />
    </>
  ),
  history: clockCounterClockwiseGlyph,
  clockCounterClockwise: clockCounterClockwiseGlyph,
  // Epic 7 re-skin (ADO-903) — hamburger for the "Browse" mega-menu trigger.
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  // Epic 7 re-skin (ADO-903) — storefront for the "Shop" home control.
  storefront: (
    <>
      <path d="M4 9l1.2-4h13.6L20 9" />
      <path d="M4 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
      <path d="M5 11v8h14v-8" />
      <path d="M10 19v-4h4v4" />
    </>
  ),
  // Epic 7 re-skin (ADO-903) — camera/scan button in the search bar.
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  // Epic 7 re-skin (ADO-903) — bar chart for the product-card "Stock" button.
  chartBar: (
    <>
      <path d="M4 20h16" />
      <rect x="6" y="11" width="3" height="6" />
      <rect x="11" y="7" width="3" height="10" />
      <rect x="16" y="13" width="3" height="4" />
    </>
  ),
  // Epic 7 re-skin (ADO-903) — grid view toggle (2×2 tiles).
  gridView: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="4" width="7" height="7" rx="1" />
      <rect x="4" y="13" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
    </>
  ),
  // Epic 7 re-skin (ADO-903) — list view toggle (stacked rows).
  listView: (
    <>
      <path d="M8 6h12M8 12h12M8 18h12" />
      <circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  // Epic 7 re-skin (ADO-903) — sliders (mobile "Filters" trigger).
  sliders: (
    <>
      <path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h8M16 17h4" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="14" cy="17" r="2" />
    </>
  ),
  // Epic 7 pixel parity (ADO-903) — right arrow (typeahead "View all" row).
  arrowRight: <path d="M4 12h15M13 6l6 6-6 6" />,
  // ADO-3985: the down arrow of "Show me ↓" (16, 19), `arrowRight` turned a quarter.
  arrowDown: <path d="M12 4v15M6 13l6 6 6-6" />,
  // ADO-1779: Phosphor ArrowLeft (the mobile "Return to products" text link): the
  // Figma vector's shaft and head, scaled from its 14 grid onto the 24 viewBox.
  arrowLeft: <path d="M20.25 12H3.5M10.5 5.25L3.75 12l6.75 6.75" />,
  deviceMobile: (
    <>
      <rect x="6" y="2.5" width="12" height="19" rx="2" />
      <path d="M6 5.5h12M6 18.5h12" />
    </>
  ),
  // Story 8.10 (ADO-2019): the Lucide-derived ShoppingBasket (Story 8.6's export, a FILLED
  // outline on a 16px grid scaled 1.5x onto the 24px viewBox) for the Ambient depot mark.
  shoppingBasket: (
    <g transform="scale(1.5)" fill="currentColor" stroke="none">
      <path d="M10.0736 6.67057C10.4395 6.71123 10.7034 7.04099 10.6628 7.4069L9.99609 13.4069C9.95544 13.7728 9.62568 14.0367 9.25977 13.9961C8.89385 13.9554 8.62996 13.6257 8.67057 13.2598L9.33724 7.25977C9.3779 6.89385 9.70766 6.62996 10.0736 6.67057Z" />
      <path d="M9.66927 2.08789C9.98895 1.90522 10.3961 2.01626 10.5788 2.33594L13.2454 7.0026C13.4281 7.32228 13.3171 7.72944 12.9974 7.91211C12.6777 8.09478 12.2706 7.98374 12.0879 7.66406L9.42122 2.9974C9.23855 2.67772 9.34959 2.27056 9.66927 2.08789Z" />
      <path d="M14.6667 6.66667C15.0349 6.66667 15.3333 6.96514 15.3333 7.33333C15.3333 7.70152 15.0349 8 14.6667 8H1.33333C0.965144 8 0.666667 7.70152 0.666667 7.33333C0.666667 6.96514 0.965144 6.66667 1.33333 6.66667H14.6667Z" />
      <path d="M13.0833 7.18424C13.1658 6.8254 13.524 6.60116 13.8828 6.68359C14.2415 6.76611 14.4652 7.12368 14.3828 7.48242L13.25 12.416L13.2487 12.4154C13.1527 12.8675 12.9042 13.2729 12.543 13.5618C12.1811 13.8513 11.73 14.0055 11.2669 13.9993L4.73307 14C4.26997 14.0062 3.8189 13.8513 3.45703 13.5618C3.09165 13.2695 2.84024 12.8583 2.74674 12.3997L1.68164 7.47396C1.604 7.11418 1.83292 6.75943 2.19271 6.68164C2.55249 6.604 2.90723 6.83292 2.98503 7.19271L4.05143 12.1257L4.05339 12.1335L4.06706 12.1901C4.10562 12.32 4.18322 12.4356 4.28971 12.5208C4.41153 12.6183 4.56409 12.6698 4.72005 12.6667H11.2799C11.4359 12.6698 11.5885 12.6183 11.7103 12.5208C11.832 12.4234 11.9155 12.2862 11.9466 12.1335C11.9477 12.1281 11.9493 12.1225 11.9505 12.1172L13.0833 7.18424Z" />
      <path d="M13 9.66667C13.3682 9.66667 13.6667 9.96514 13.6667 10.3333C13.6667 10.7015 13.3682 11 13 11H3C2.63181 11 2.33333 10.7015 2.33333 10.3333C2.33333 9.96514 2.63181 9.66667 3 9.66667H13Z" />
      <path d="M5.42122 2.33594C5.6039 2.01626 6.01105 1.90522 6.33073 2.08789C6.65041 2.27056 6.76145 2.67772 6.57878 2.9974L3.91211 7.66406C3.72944 7.98374 3.32228 8.09478 3.0026 7.91211C2.68293 7.72944 2.57188 7.32228 2.75456 7.0026L5.42122 2.33594Z" />
      <path d="M5.92643 6.67057C6.29234 6.62996 6.6221 6.89385 6.66276 7.25977L7.32943 13.2598C7.37004 13.6257 7.10615 13.9554 6.74023 13.9961C6.37433 14.0367 6.04456 13.7728 6.00391 13.4069L5.33724 7.4069C5.29662 7.04099 5.56052 6.71123 5.92643 6.67057Z" />
    </g>
  ),
  // Story 8.10 (ADO-2019): Lucide Thermometer and Wine, path for path on the 24px grid, at
  // Lucide's 2px stroke so they carry the same weight as `shoppingBasket`.
  thermometer: (
    <g strokeWidth={2}>
      <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" />
    </g>
  ),
  wine: (
    <g strokeWidth={2}>
      <path d="M8 22h8" />
      <path d="M7 10h10" />
      <path d="M12 15v7" />
      <path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z" />
    </g>
  ),
  // Story 9.6 (ADO-1080) — Phosphor Calendar: a page with two binding posts and the
  // rule under the header band.
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 9.5h17M8 3.5V6.5M16 3.5V6.5" />
    </>
  ),
  // Story 9.6 (ADO-1080) — Phosphor ArrowsClockwise: two arcs chasing each other, each
  // ending in a head, drawn as a broken ring so it reads as "reset" rather than "undo".
  arrowsClockwise: (
    <>
      <path d="M20 11.5a8 8 0 0 0-13.7-5.2L3.5 9" />
      <path d="M3.5 4.5V9H8" />
      <path d="M4 12.5a8 8 0 0 0 13.7 5.2l2.8-2.7" />
      <path d="M20.5 19.5V15H16" />
    </>
  ),
  // ADO-1779 — Figma Grid Icon: 5×5 tiles (radius 1.5) at 1/10 on a 16 grid,
  // scaled ×1.5 onto the 24 viewBox. Filled, so the stroke is switched off.
  depots: (
    <g fill="currentColor" stroke="none">
      <rect x="1.5" y="1.5" width="7.5" height="7.5" rx="2.25" />
      <rect x="15" y="1.5" width="7.5" height="7.5" rx="2.25" />
      <rect x="1.5" y="15" width="7.5" height="7.5" rx="2.25" />
      <rect x="15" y="15" width="7.5" height="7.5" rx="2.25" />
    </g>
  ),
  // Epic 9 UI polish — Phosphor Package (Figma 598:31), the exact Figma export rather than a
  // stroke approximation. It is a FILLED outline drawn on a 16px grid, so it scales 1.5x onto
  // the 24px viewBox and turns the registry's stroke off in favour of a currentColor fill.
  package: (
    <path
      transform="scale(1.5)"
      fill="currentColor"
      stroke="none"
      d="M14.1 3.915L8.6 0.904375C8.4162 0.803215 8.2098 0.750169 8 0.750169C7.7902 0.750169 7.5838 0.803215 7.4 0.904375L1.9 3.915C1.7032 4.02268 1.539 4.18134 1.42464 4.37433C1.31027 4.56733 1.24995 4.78754 1.25 5.01187V10.9881C1.24995 11.2125 1.31027 11.4327 1.42464 11.6257C1.539 11.8187 1.7032 11.9773 1.9 12.085L7.4 15.0956C7.58377 15.1969 7.79018 15.25 8 15.25C8.20982 15.25 8.41623 15.1969 8.6 15.0956L14.1 12.085C14.2968 11.9773 14.461 11.8187 14.5754 11.6257C14.6897 11.4327 14.7501 11.2125 14.75 10.9881V5.01187C14.7501 4.78754 14.6897 4.56733 14.5754 4.37433C14.461 4.18134 14.2968 4.02268 14.1 3.915ZM8 2.28562L12.5 4.75L11.1606 5.48312L6.66062 3.01938L8 2.28562ZM8 7.2125L3.5 4.75L5.0975 3.875L9.5975 6.33812L8 7.2125ZM2.75 6.04937L7.25 8.51187V13.3037L2.75 10.84V6.04937ZM8.75 13.3037V8.51187L10.25 7.69125V9.5C10.25 9.69891 10.329 9.88968 10.4697 10.0303C10.6103 10.171 10.8011 10.25 11 10.25C11.1989 10.25 11.3897 10.171 11.5303 10.0303C11.671 9.88968 11.75 9.69891 11.75 9.5V6.87L13.25 6.04937V10.84L8.75 13.3037Z"
    />
  ),
  // Story 8.6 (ADO-2015) — the exact Figma export, like `package`: a FILLED outline, the badge
  // check drawn on an 18px grid (scaled 4/3). `checkBold` is Figma's Check component, heavier
  // than the stroked `check` approximation above.
  checkBold: (
    <g transform="scale(1.3333)" fill="currentColor" stroke="none">
      <path d="M16.347 5.65945L7.34695 14.6595C7.26857 14.7381 7.17542 14.8005 7.07286 14.8431C6.9703 14.8857 6.86035 14.9076 6.7493 14.9076C6.63825 14.9076 6.52829 14.8857 6.42573 14.8431C6.32317 14.8005 6.23003 14.7381 6.15164 14.6595L2.21414 10.722C2.13566 10.6435 2.0734 10.5503 2.03092 10.4477C1.98845 10.3452 1.96658 10.2353 1.96658 10.1243C1.96658 10.0133 1.98845 9.90339 2.03092 9.80085C2.0734 9.6983 2.13566 9.60513 2.21414 9.52664C2.29263 9.44816 2.3858 9.3859 2.48835 9.34342C2.59089 9.30095 2.7008 9.27908 2.8118 9.27908C2.92279 9.27908 3.0327 9.30095 3.13525 9.34342C3.23779 9.3859 3.33097 9.44816 3.40945 9.52664L6.75 12.8672L15.153 4.46555C15.3116 4.30704 15.5265 4.21799 15.7507 4.21799C15.9749 4.21799 16.1899 4.30704 16.3484 4.46555C16.5069 4.62406 16.5959 4.83904 16.5959 5.0632C16.5959 5.28737 16.5069 5.50235 16.3484 5.66086L16.347 5.65945Z" />
    </g>
  ),
  // Story 8.12 (ADO-2021) — the exact Figma export (Truck in Cart Row 4977:1802), FILLED, drawn on
  // a 14px grid (scaled 24/14 onto the 24px viewBox).
  truck: (
    <g transform="scale(1.7143)" fill="currentColor" stroke="none">
      <path d="M7.58333 10.5V3.5C7.58333 3.34529 7.52183 3.19696 7.41243 3.08757C7.30304 2.97817 7.15471 2.91667 7 2.91667H2.33333C2.17862 2.91667 2.03029 2.97817 1.9209 3.08757C1.8115 3.19696 1.75 3.34529 1.75 3.5V9.91667H2.91667C3.23883 9.91667 3.5 10.1778 3.5 10.5C3.5 10.8222 3.23883 11.0833 2.91667 11.0833H1.75C1.44058 11.0833 1.14392 10.9603 0.92513 10.7415C0.706337 10.5227 0.583333 10.2261 0.583333 9.91667V3.5C0.583333 3.03587 0.76784 2.59088 1.09603 2.2627C1.42422 1.93451 1.8692 1.75 2.33333 1.75H7C7.46413 1.75 7.90912 1.93451 8.2373 2.2627C8.56549 2.59088 8.75 3.03587 8.75 3.5V10.5C8.75 10.8222 8.48883 11.0833 8.16667 11.0833C7.8445 11.0833 7.58333 10.8222 7.58333 10.5Z" />
      <path d="M8.75 9.91667C9.07217 9.91667 9.33333 10.1778 9.33333 10.5C9.33333 10.8222 9.07217 11.0833 8.75 11.0833H5.25C4.92783 11.0833 4.66667 10.8222 4.66667 10.5C4.66667 10.1778 4.92783 9.91667 5.25 9.91667H8.75Z" />
      <path d="M12.25 9.91667V7.78841L12.2494 7.78784L10.2197 5.25057L10.2192 5.25H8.16667C7.8445 5.25 7.58333 4.98883 7.58333 4.66667C7.58333 4.3445 7.8445 4.08333 8.16667 4.08333H10.2209L10.3513 4.09074C10.481 4.10552 10.6075 4.14207 10.7256 4.19897C10.8829 4.27483 11.021 4.38552 11.13 4.52197L13.1597 7.05867L13.2184 7.13843C13.3467 7.32951 13.4163 7.55508 13.4167 7.7867V9.91667C13.4167 10.2261 13.2937 10.5227 13.0749 10.7415C12.8561 10.9603 12.5594 11.0833 12.25 11.0833H11.0833C10.7612 11.0833 10.5 10.8222 10.5 10.5C10.5 10.1778 10.7612 9.91667 11.0833 9.91667H12.25Z" />
      <path d="M10.5 10.5C10.5 10.1778 10.2388 9.91667 9.91667 9.91667C9.5945 9.91667 9.33333 10.1778 9.33333 10.5C9.33333 10.8222 9.5945 11.0833 9.91667 11.0833C10.2388 11.0833 10.5 10.8222 10.5 10.5ZM11.6667 10.5C11.6667 11.4665 10.8832 12.25 9.91667 12.25C8.95017 12.25 8.16667 11.4665 8.16667 10.5C8.16667 9.5335 8.95017 8.75 9.91667 8.75C10.8832 8.75 11.6667 9.5335 11.6667 10.5Z" />
      <path d="M4.66667 10.5C4.66667 10.1778 4.4055 9.91667 4.08333 9.91667C3.76117 9.91667 3.5 10.1778 3.5 10.5C3.5 10.8222 3.76117 11.0833 4.08333 11.0833C4.4055 11.0833 4.66667 10.8222 4.66667 10.5ZM5.83333 10.5C5.83333 11.4665 5.04983 12.25 4.08333 12.25C3.11683 12.25 2.33333 11.4665 2.33333 10.5C2.33333 9.5335 3.11683 8.75 4.08333 8.75C5.04983 8.75 5.83333 9.5335 5.83333 10.5Z" />
    </g>
  ),
  // Story 8.7 (ADO-2016) — the exact Figma exports, FILLED like `truck`: CheckCircle (Figma
  // CheckCircle2 596:7) drawn on a 20px grid (scaled 6/5) and ShoppingCart (594:34) drawn on an
  // 18px grid (scaled 4/3).
  checkCircleBold: (
    <path
      transform="scale(1.2)"
      fill="currentColor"
      stroke="none"
      d="M13.7883 7.46172C13.8757 7.54882 13.945 7.65231 13.9923 7.76626C14.0397 7.88022 14.064 8.00239 14.064 8.12578C14.064 8.24917 14.0397 8.37134 13.9923 8.4853C13.945 8.59925 13.8757 8.70275 13.7883 8.78984L9.41328 13.1648C9.32618 13.2522 9.22269 13.3216 9.10874 13.3689C8.99478 13.4162 8.87261 13.4406 8.74922 13.4406C8.62583 13.4406 8.50366 13.4162 8.3897 13.3689C8.27575 13.3216 8.17225 13.2522 8.08516 13.1648L6.21016 11.2898C6.12295 11.2026 6.05377 11.0991 6.00658 10.9852C5.95938 10.8712 5.93509 10.7491 5.93509 10.6258C5.93509 10.5025 5.95938 10.3803 6.00658 10.2664C6.05377 10.1525 6.12295 10.0489 6.21016 9.96172C6.29736 9.87451 6.40089 9.80534 6.51483 9.75814C6.62877 9.71095 6.75089 9.68665 6.87422 9.68665C6.99755 9.68665 7.11967 9.71095 7.23361 9.75814C7.34755 9.80534 7.45107 9.87451 7.53828 9.96172L8.75 11.1719L12.4617 7.45938C12.5489 7.37236 12.6525 7.3034 12.7664 7.25642C12.8802 7.20944 13.0023 7.18537 13.1255 7.18559C13.2487 7.18581 13.3706 7.21031 13.4844 7.25769C13.5981 7.30507 13.7014 7.3744 13.7883 7.46172ZM18.4375 10C18.4375 11.6688 17.9426 13.3001 17.0155 14.6876C16.0884 16.0752 14.7706 17.1566 13.2289 17.7952C11.6871 18.4338 9.99064 18.6009 8.35393 18.2754C6.71721 17.9498 5.21379 17.1462 4.03379 15.9662C2.85378 14.7862 2.05019 13.2828 1.72462 11.6461C1.39906 10.0094 1.56615 8.31286 2.20477 6.77111C2.84338 5.22936 3.92484 3.9116 5.31238 2.98448C6.69992 2.05735 8.33122 1.5625 10 1.5625C12.237 1.56498 14.3817 2.45473 15.9635 4.03653C17.5453 5.61833 18.435 7.763 18.4375 10ZM16.5625 10C16.5625 8.70206 16.1776 7.43327 15.4565 6.35407C14.7354 5.27487 13.7105 4.43374 12.5114 3.93704C11.3122 3.44034 9.99272 3.31038 8.71972 3.5636C7.44672 3.81681 6.27739 4.44183 5.35961 5.35961C4.44183 6.27739 3.81681 7.44672 3.5636 8.71972C3.31038 9.99272 3.44034 11.3122 3.93704 12.5114C4.43374 13.7105 5.27487 14.7354 6.35407 15.4565C7.43327 16.1776 8.70206 16.5625 10 16.5625C11.7399 16.5606 13.408 15.8686 14.6383 14.6383C15.8686 13.408 16.5606 11.7399 16.5625 10Z"
    />
  ),
  shoppingCart: (
    <path
      transform="scale(1.3333)"
      fill="currentColor"
      stroke="none"
      d="M16.3976 3.9593C16.3184 3.86445 16.2194 3.78815 16.1075 3.73579C15.9956 3.68342 15.8735 3.65627 15.75 3.65625H4.64062L4.25602 1.53633C4.22063 1.34204 4.11822 1.16632 3.96661 1.03976C3.81501 0.91321 3.62381 0.843846 3.42633 0.84375H1.6875C1.46372 0.84375 1.24911 0.932645 1.09088 1.09088C0.932645 1.24911 0.84375 1.46372 0.84375 1.6875C0.84375 1.91128 0.932645 2.12589 1.09088 2.28412C1.24911 2.44236 1.46372 2.53125 1.6875 2.53125H2.7218L4.47328 12.1641C4.52031 12.4199 4.61757 12.6639 4.75945 12.882C4.50826 13.1759 4.33704 13.5296 4.26231 13.9089C4.18759 14.2882 4.21187 14.6804 4.33283 15.0477C4.45378 15.4149 4.66732 15.7447 4.95286 16.0054C5.23839 16.2661 5.58628 16.4488 5.96297 16.5359C6.33966 16.623 6.73245 16.6116 7.10343 16.5027C7.47442 16.3939 7.8111 16.1912 8.08099 15.9144C8.35088 15.6375 8.54488 15.2958 8.64427 14.9222C8.74366 14.5485 8.7451 14.1556 8.64844 13.7812H11.0391C10.9218 14.2355 10.9494 14.7152 11.1182 15.153C11.2869 15.5908 11.5883 15.9649 11.9802 16.2229C12.372 16.481 12.8348 16.61 13.3036 16.5921C13.7725 16.5742 14.224 16.4101 14.595 16.1229C14.966 15.8356 15.2379 15.4396 15.3727 14.9902C15.5076 14.5408 15.4985 14.0605 15.3468 13.6165C15.1952 13.1724 14.9085 12.787 14.5269 12.5139C14.1453 12.2409 13.6879 12.094 13.2188 12.0938H6.41039C6.34464 12.0937 6.28099 12.0706 6.2305 12.0284C6.18002 11.9863 6.1459 11.9278 6.13406 11.8631L5.97094 10.9688H13.7883C14.2494 10.9688 14.696 10.8069 15.05 10.5113C15.404 10.2158 15.643 9.80532 15.7254 9.35156L16.5804 4.65047C16.6024 4.5289 16.5974 4.40399 16.5658 4.28456C16.5342 4.16513 16.4768 4.0541 16.3976 3.9593ZM7.03125 14.3438C7.03125 14.455 6.99826 14.5638 6.93645 14.6563C6.87464 14.7488 6.78679 14.8209 6.68401 14.8634C6.58123 14.906 6.46813 14.9171 6.35901 14.8954C6.2499 14.8737 6.14967 14.8202 6.071 14.7415C5.99234 14.6628 5.93876 14.5626 5.91706 14.4535C5.89535 14.3444 5.90649 14.2313 5.94907 14.1285C5.99164 14.0257 6.06374 13.9379 6.15624 13.876C6.24874 13.8142 6.3575 13.7812 6.46875 13.7812C6.61793 13.7812 6.76101 13.8405 6.8665 13.946C6.97199 14.0515 7.03125 14.1946 7.03125 14.3438ZM13.2188 14.9062C13.1075 14.9062 12.9987 14.8733 12.9062 14.8115C12.8137 14.7496 12.7416 14.6618 12.6991 14.559C12.6565 14.4562 12.6454 14.3431 12.6671 14.234C12.6888 14.1249 12.7423 14.0247 12.821 13.946C12.8997 13.8673 12.9999 13.8138 13.109 13.7921C13.2181 13.7704 13.3312 13.7815 13.434 13.8241C13.5368 13.8666 13.6246 13.9387 13.6865 14.0312C13.7483 14.1237 13.7812 14.2325 13.7812 14.3438C13.7812 14.4929 13.722 14.636 13.6165 14.7415C13.511 14.847 13.3679 14.9062 13.2188 14.9062ZM14.0625 9.05063C14.0507 9.11495 14.0169 9.17317 13.9669 9.21526C13.9168 9.25735 13.8537 9.28068 13.7883 9.28125H5.66437L4.94859 5.34375H14.7389L14.0625 9.05063Z"
    />
  ),
  // ADO-1779 — the exact Figma export (ArrowUp 595:16), FILLED like `checkCircleBold`, drawn on
  // a 20px grid (scaled 6/5).
  arrowUp: (
    <path
      transform="scale(1.2)"
      fill="currentColor"
      stroke="none"
      d="M16.2883 9.41328C16.2012 9.50068 16.0977 9.57003 15.9837 9.61735C15.8698 9.66466 15.7476 9.68902 15.6242 9.68902C15.5008 9.68902 15.3787 9.66466 15.2647 9.61735C15.1507 9.57003 15.0473 9.50068 14.9602 9.41328L10.9375 5.39062V16.875C10.9375 17.1236 10.8387 17.3621 10.6629 17.5379C10.4871 17.7137 10.2486 17.8125 10 17.8125C9.75136 17.8125 9.5129 17.7137 9.33709 17.5379C9.16127 17.3621 9.0625 17.1236 9.0625 16.875V5.39062L5.03828 9.41328C4.86216 9.5894 4.62329 9.68834 4.37422 9.68834C4.12515 9.68834 3.88628 9.5894 3.71016 9.41328C3.53404 9.23716 3.43509 8.99829 3.43509 8.74922C3.43509 8.50015 3.53404 8.26128 3.71016 8.08516L9.33516 2.46016C9.42225 2.37276 9.52575 2.30341 9.6397 2.25609C9.75366 2.20877 9.87583 2.18442 9.99922 2.18442C10.1226 2.18442 10.2448 2.20877 10.3587 2.25609C10.4727 2.30341 10.5762 2.37276 10.6633 2.46016L16.2883 8.08516C16.3757 8.17225 16.445 8.27575 16.4923 8.3897C16.5397 8.50366 16.564 8.62583 16.564 8.74922C16.564 8.87261 16.5397 8.99478 16.4923 9.10874C16.445 9.22269 16.3757 9.32618 16.2883 9.41328Z"
    />
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Pixel size of the square icon. Defaults to 20. */
  size?: number;
  /** When set, the icon becomes an accessible graphic (role="img") with this label. */
  title?: string;
}

export function Icon({ name, size = 20, title, ...rest }: IconProps) {
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...a11y}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
